import { useCallback, useEffect, useRef, useState } from 'react';
import { approvalsApi } from '../../../shared/components/approvals/approvalsApi';

/*
  Deck state for a queue: the light list of documents, the current index, progress, and navigation.
  A decision removes the current card (index clamps); skip moves on and wraps around; the list is only refetched
  when it runs out, when the tab regains focus, or when the source changes.
*/
export function useApprovalQueue({ docType, source = 'queue', lane = 'unprocessed', initialId = null }) {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialTotal, setInitialTotal] = useState(0);
  const [decided, setDecided] = useState(0);
  const pendingInitialId = useRef(initialId ? Number(initialId) : null);

  const load = useCallback(
    async ({ keepProgress = false } = {}) => {
      setLoading(true);
      const response = source === 'intake' ? await approvalsApi.intake(docType, lane) : await approvalsApi.queue(docType);
      setLoading(false);
      if (!response.ok) {
        setError(response.error);
        return;
      }
      const list = Array.isArray(response.data) ? response.data : [];
      setError(null);
      setItems(list);
      if (!keepProgress) {
        setInitialTotal(list.length);
        setDecided(0);
      }
      setIndex((current) => {
        if (pendingInitialId.current) {
          const found = list.findIndex((item) => Number(item.id) === pendingInitialId.current);
          pendingInitialId.current = null;
          if (found >= 0) return found;
        }
        return Math.min(current, Math.max(0, list.length - 1));
      });
    },
    [docType, source, lane]
  );

  useEffect(() => {
    pendingInitialId.current = initialId ? Number(initialId) : null;
    setIndex(0);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, source, lane]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') load({ keepProgress: true });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [load]);

  const removeCurrent = useCallback(() => {
    setItems((previous) => {
      const next = previous.filter((_, i) => i !== index);
      setIndex((current) => Math.min(current, Math.max(0, next.length - 1)));
      return next;
    });
    setDecided((count) => count + 1);
  }, [index]);

  const replaceCurrent = useCallback((patch) => setItems((previous) => previous.map((item, i) => (i === index ? { ...item, ...patch } : item))), [index]);

  const skip = useCallback(() => setIndex((current) => (items.length ? (current + 1) % items.length : 0)), [items.length]);
  const next = useCallback(() => setIndex((current) => Math.min(current + 1, Math.max(0, items.length - 1))), [items.length]);
  const prev = useCallback(() => setIndex((current) => Math.max(0, current - 1)), []);

  return {
    items,
    index,
    current: items[index] || null,
    nextItem: items[index + 1] || (items.length > 1 ? items[0] : null),
    total: items.length,
    initialTotal,
    decided,
    loading,
    error,
    reload: load,
    removeCurrent,
    replaceCurrent,
    skip,
    next,
    prev,
    setIndex
  };
}
